<script>
import ProductHelper from "@/utils/productHelper";
import PartCard from "./PartCard";

export default {
  components: { PartCard },
  props: {
    product: { type: Object, required: true },
    currentStep: { type: Object, default: null },
    alertMode: { type: Boolean, default: false },
    inlineDetailOpen: { type: Boolean, default: false },
    config: { type: Object, default: null },
    canUndo: { type: Boolean, default: false },
  },
  computed: {
    damagedParts() {
      return (this.product.parts || []).filter((p) => p.isDamaged);
    },
    backOrderParts() {
      return (this.product.parts || []).filter((p) => p.isBackOrder);
    },
    allPartsDamaged() {
      const parts = this.product.parts || [];
      return parts.length > 0 && this.damagedParts.length === parts.length;
    },
    allPartsBackOrder() {
      const parts = this.product.parts || [];
      return parts.length > 0 && this.backOrderParts.length === parts.length;
    },
    isProductScanned() {
      return ProductHelper.isProductStepCompleted(
        this.product,
        this.currentStep,
      );
    },
    isAlerted() {
      return this.product.isDamaged || this.product.isBackOrder;
    },
    isDimmed() {
      return (this.alertMode && !this.isAlerted ) || ProductHelper.hasNoProductStep(this.product, this.currentStep);
    },
    collapseId() {
      return "extras-" + this.product.idPublic;
    },
    iconClass() {
      return this.product.isDetached
        ? "mdi mdi-puzzle-outline"
        : "mdi mdi-cube-outline";
    },
    startExpanded() {
      return !this.config?.collapseByDefault;
    },
    genericExtraRows() {
      const rows = [
        { key: "workOrderName", label: this.$t("general.workorder"), value: this.product.workOrderName },
        { key: "weight", label: this.$t("general.weight"), value: this.product.weight },
        { key: "comment2", label: `${this.$t("general.comment")} 2`, value: this.product.comment2 },
        { key: "comment3", label: `${this.$t("general.comment")} 3`, value: this.product.comment3 },
        ...Array.from({ length: 20 }, (_, i) => ({
          key: "info" + (i + 1),
          label: `${this.$t("general.info")} ${i + 1}`,
          value: this.product["info" + (i + 1)],
        })),
      ];
      return rows.filter(
        (r) =>
          (r.value || r.value === 0) && this.fieldVisible(r.key),
      );
    },
  },
  methods: {
    fieldVisible(name) {
      return this.config?.visibleFields?.[name] !== false;
    },
    onCardClick() {
      this.$emit("card-click");
    },
    onCheck() {
      if (this.isProductScanned) {
        if (this.canUndo) {
          this.$emit("uncheck");
        }
      } else {
        this.$emit("check");
      }
    },
    onToggleInlineDetail() {
      this.$emit("toggle-inline-detail");
    },
    onOpenAlert() {
      this.$emit("open-alert");
    },
  },
};
</script>
<template>
  <div
    class="icard"
    :class="{ scanned: isProductScanned, 'dmg-hl': isAlerted, dim: isDimmed }"
    @click="onCardClick"
  >
    <div class="imain">
      <div class="iico">
        <i :class="iconClass"></i>
      </div>
      <div class="iinfo">
        <div class="iname">{{ product.name }}</div>
        <div class="ienv" v-if="product.room && fieldVisible('room')">
          <strong>{{ $t("general.environment") }}:</strong>
          {{ product.room }}
        </div>
        <div class="ienv" v-if="product.kitName && fieldVisible('kit')">
          <strong>{{ $t("general.kit") }}:</strong>
          {{ product.kitName }}
        </div>
      </div>
      <div class="iright">
        <span
          v-if="product.isDamaged"
          class="abubble abubble-dmg"
          role="button"
          @click.stop="onToggleInlineDetail"
        >
          <i class="mdi mdi-alert-outline"></i>
        </span>
        <span
          v-if="product.isBackOrder"
          class="abubble abubble-bo"
          role="button"
          @click.stop="onToggleInlineDetail"
        >
          <i class="mdi mdi-minus-circle-outline"></i>
        </span>
        <span
          class="chkbox"
          :class="{ checked: isProductScanned }"
          role="button"
          @click.stop="onCheck"
        >
          <i class="mdi mdi-check" v-if="isProductScanned"></i>
        </span>
        <button v-b-toggle="collapseId" class="expbtn" @click.stop>
          <i class="mdi mdi-chevron-down expbtn-icon"></i>
        </button>
      </div>
    </div>

    <div v-if="product.isDamaged && inlineDetailOpen" class="dmg-detail">
      <div class="dmg-h">
        <i class="mdi mdi-alert-outline text-warning"></i>
        <span class="dmg-title">{{ $t("general.damaged") }}</span>
      </div>
      <div class="dmg-row">
        <span class="dmg-lbl">{{ $t("general.reason") }}</span>
        <span class="dmg-val">{{ product.reasonName || "—" }}</span>
      </div>
      <div class="dmg-row">
        <span class="dmg-lbl">{{ $t("general.comment") }}</span>
        <span class="dmg-val">{{ product.problemNote || "—" }}</span>
      </div>
      <div class="dmg-parts" v-if="damagedParts.length" @click.stop>
        <div class="dmg-parts-h">
          {{ allPartsDamaged ? $t("general.allparts") : $t("general.parts") }}
        </div>
        <PartCard
          v-for="part in damagedParts"
          :key="part.idPublic"
          :part="part"
          :config="config"
        />
      </div>
      <div class="dmg-actions">
        <button class="dmg-btn dmg-btn-outline" @click.stop="onOpenAlert">
          <i class="mdi mdi-pencil-outline"></i>
          {{ $t("general.alert") }}
        </button>
      </div>
    </div>

    <div
      v-if="product.isBackOrder && inlineDetailOpen"
      class="dmg-detail bo-detail"
    >
      <div class="dmg-h">
        <i class="mdi mdi-minus-circle-outline text-danger"></i>
        <span class="dmg-title dmg-title-bo">{{ $t("general.bo") }}</span>
      </div>
      <div class="dmg-row">
        <span class="dmg-lbl">{{ $t("general.comment") }}</span>
        <span class="dmg-val">{{ product.problemNote || "—" }}</span>
      </div>
      <div class="dmg-parts" v-if="backOrderParts.length" @click.stop>
        <div class="dmg-parts-h">
          {{ allPartsBackOrder ? $t("general.allparts") : $t("general.parts") }}
        </div>
        <PartCard
          v-for="part in backOrderParts"
          :key="part.idPublic"
          :part="part"
          :config="config"
        />
      </div>
      <div class="dmg-actions">
        <button
          class="dmg-btn dmg-btn-bo-outline"
          @click.stop="onOpenAlert"
        >
          <i class="mdi mdi-pencil-outline"></i>
          {{ $t("general.alert") }}
        </button>
      </div>
    </div>

    <b-collapse :id="collapseId" :visible="startExpanded">
      <div class="iextra">
        <div
          class="exrow"
          v-if="(product.width || product.height || product.depth) && fieldVisible('dimensions')"
        >
          <span class="exlbl">{{ $t("general.dimensions") }}</span>
          <span class="exval">
            {{ product.width || 0 }} ×
            {{ product.height || 0 }} ×
            {{ product.depth || 0 }}
          </span>
        </div>
        <div class="exrow" v-if="product.comment && fieldVisible('comment')">
          <span class="exlbl">{{ $t("general.comment") }}</span>
          <span class="exval">{{ product.comment }}</span>
        </div>
        <div class="exrow" v-if="product.barCode && fieldVisible('barcode')">
          <span class="exlbl">{{ $t("general.barcode") }}</span>
          <span class="exval">{{ product.barCode }}</span>
        </div>
        <div
          class="exrow"
          v-if="product.rfidBarCode && fieldVisible('rfidBarcode')"
        >
          <span class="exlbl">{{ $t("general.rfidbarcode") }}</span>
          <span class="exval">{{ product.rfidBarCode }}</span>
        </div>
        <div
          class="exrow"
          v-if="product.kitBarcode && fieldVisible('kitBarcode')"
        >
          <span class="exlbl">{{ $t("general.kitbarcode") }}</span>
          <span class="exval">{{ product.kitBarcode }}</span>
        </div>
        <div
          class="exrow"
          v-if="product.packingName && fieldVisible('packing')"
        >
          <span class="exlbl">{{ $t("general.packinglist") }}</span>
          <span class="exval">{{ product.packingName }}</span>
        </div>
        <div class="exrow" v-for="row in genericExtraRows" :key="row.key">
          <span class="exlbl">{{ row.label }}</span>
          <span class="exval">{{ row.value }}</span>
        </div>
      </div>
    </b-collapse>
  </div>
</template>
<style scoped>
.icard {
  background: white;
  border-radius: 11px;
  border: 1.5px solid #e0e5ea;
  overflow: hidden;
  transition: border-color 0.2s, background 0.2s, opacity 0.2s;
}
.icard.scanned {
  background: #e6faf3;
  border-color: #a9dfc3;
}
.icard.dim {
  opacity: 0.5;
}
.imain {
  padding: 9px 11px;
  display: flex;
  align-items: flex-start;
  gap: 9px;
}
.iico {
  width: 30px;
  height: 30px;
  background: #f0f4f7;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7a8a99;
  font-size: 16px;
  flex-shrink: 0;
}
.icard.scanned .iico {
  background: rgba(52, 195, 143, 0.12);
  color: #34c38f;
}
.iinfo {
  flex: 1;
  min-width: 0;
}
.iname {
  font-size: 12px;
  font-weight: 700;
  color: #1c2e3e;
}
.ienv {
  font-size: 11px;
  color: #7a8a99;
  margin-top: 1px;
}
.ienv strong {
  color: #1c2e3e;
  font-weight: 600;
}
.iright {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}
.abubble {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 15px;
}
.abubble-dmg {
  background: #fff8e1;
  color: #f1b44c;
  border: 1px solid #f9d77e;
}
.abubble-bo {
  background: #fdecea;
  color: #e74c3c;
  border: 1px solid #f9bdbd;
}
.chkbox {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1.5px solid #e0e5ea;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: transparent;
}
.chkbox.checked {
  background: #34c38f;
  border-color: #34c38f;
  color: white;
}
.expbtn {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid #e0e5ea;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7a8a99;
  font-size: 13px;
  padding: 0;
}
.expbtn-icon {
  transition: transform 0.2s;
}
.expbtn.not-collapsed .expbtn-icon {
  transform: rotate(180deg);
}
.dmg-detail {
  background: #fff8e1;
  border-top: 1px solid #f9d77e;
  padding: 8px 11px;
}
.bo-detail {
  background: #fdecea;
  border-top-color: #f9bdbd;
}
.dmg-h {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 5px;
}
.dmg-title {
  font-size: 11px;
  font-weight: 700;
  color: #b07d00;
}
.dmg-title-bo {
  color: #e74c3c;
}
.dmg-row {
  display: flex;
  gap: 7px;
  margin-bottom: 3px;
}
.dmg-lbl {
  font-size: 10px;
  color: #7a8a99;
  min-width: 76px;
  flex-shrink: 0;
}
.dmg-val {
  font-size: 11px;
  color: #1c2e3e;
  font-weight: 500;
}
.dmg-parts {
  margin-top: 7px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.dmg-parts-h {
  font-size: 10px;
  font-weight: 600;
  color: #7a8a99;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.dmg-actions {
  display: flex;
  gap: 5px;
  margin-top: 7px;
}
.dmg-btn {
  padding: 4px 9px;
  border-radius: 7px;
  font-size: 10px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 3px;
}
.dmg-btn-outline {
  background: white;
  color: #f1b44c;
  border: 1px solid #f9d77e;
}
.dmg-btn-bo-outline {
  background: white;
  color: #e74c3c;
  border: 1px solid #f9bdbd;
}
.iextra {
  border-top: 1px solid #e0e5ea;
  padding: 8px 11px;
  background: rgba(0, 0, 0, 0.012);
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.icard.scanned .iextra {
  border-top-color: #a9dfc3;
  background: rgba(52, 195, 143, 0.04);
}
.exrow {
  display: flex;
  gap: 7px;
  align-items: flex-start;
}
.exlbl {
  font-size: 10px;
  color: #7a8a99;
  min-width: 78px;
  flex-shrink: 0;
  margin-top: 1px;
}
.exval {
  font-size: 11px;
  color: #1c2e3e;
  flex: 1;
  font-weight: 500;
}
</style>
